import { reactive, ref } from 'vue';
import { fullScreenLoader, removeFullScreenLoader } from '@/components/jsComponents';
import { markLeavingFor } from '@/code/back-forward';
import { showToast } from '@/components/toast';
import { skapi } from '@/main';
import { serviceList, serviceIdList, endOfList } from '@/views/project-list';

import { miscUpdater, SITE, homeSiteOf, touchedSites, touchSite } from '@/code/misc';

export let user: { [key: string]: any } = reactive({});
export let customer: Promise<Customer> = null;
let lastLoggedInUserId: string | null = null;

type Customer = {
	id: string;
	object: 'customer';
	address: {
		city: string;
		country: string;
		line1: string;
		line2: string;
		postal_code: string;
		state: string;
	};
	balance: number;
	created: number;
	currency: string;
	default_source: any;
	delinquent: boolean;
	description: any;
	discount: any;
	email: string;
	invoice_prefix: string;
	invoice_settings: {
		custom_fields: any;
		default_payment_method: any;
		footer: any;
		rendering_options: any;
	};
	livemode: boolean;
	metadata: {
		[key: string]: any;
	};
	name: string;
	next_invoice_sequence: number;
	phone: any;
	preferred_locales: any[];
	shipping: any;
	tax_exempt: string;
	test_clock: any;
};

export let initUser = (userIncoming: any) => {
	for (let k in user) {
		delete user[k];
	}
	if (userIncoming) {
		for (let k in userIncoming) {
			user[k] = userIncoming[k];
		}
	}
};

// ---- Newsletters -------------------------------------------------------------
// The host project runs two named newsletter groups, one per site, registered on
// dev and prod on 2026-10-06: "bq" is BunnyQuery's, "sp" is Skapi's. A new account
// is signed up to the group of the site it joins on (signup.vue, and the first
// Google login below), and the account page offers both. Group 1 ("authorized",
// Service Email) is what signups used to join; the page no longer offers it.
export const NEWSLETTER_GROUPS = {
	bunnyquery: { group: 'bq', label: 'BunnyQuery newsletter' },
	skapi: { group: 'sp', label: 'Skapi newsletter' },
} as const;
export type NewsletterGroup = (typeof NEWSLETTER_GROUPS)[keyof typeof NEWSLETTER_GROUPS]['group'];
/** The newsletter this site signs new accounts up to. */
export const SITE_NEWSLETTER_GROUP: NewsletterGroup = NEWSLETTER_GROUPS.skapi.group;
/** The order the account page lists them in: this site's own first. */
export const NEWSLETTER_PAGE_ORDER = [NEWSLETTER_GROUPS.skapi, NEWSLETTER_GROUPS.bunnyquery];

// One answer per group for the life of the page, as the single cached promise
// was before; a failed read is dropped so the next look asks again.
const newsletterSubscriptionStatus = new Map<string, Promise<boolean>>();
export function getNewsletterSubscriptionStatus(group: string = SITE_NEWSLETTER_GROUP) {
	let cached = newsletterSubscriptionStatus.get(group);
	if (!cached) {
		cached = skapi
			.getNewsletterSubscription({ group })
			// one group asked for: the SDK answers a plain array; a paged answer
			// (the `list` shape) only comes back for an owner's address search
			.then((subs) => {
				const list = Array.isArray(subs) ? subs : subs?.list;
				return Array.isArray(list) && list.length > 0;
			})
			.catch((err) => {
				newsletterSubscriptionStatus.delete(group);
				throw err;
			});
		newsletterSubscriptionStatus.set(group, cached);
	}
	return cached;
}

export function setNewsletterSubscriptionStatus(status: boolean, group: string = SITE_NEWSLETTER_GROUP) {
	const call = status ? skapi.subscribeNewsletter({ group }) : skapi.unsubscribeNewsletter({ group });
	return call.then(() => {
		newsletterSubscriptionStatus.set(group, Promise.resolve(status));
	});
}

// FIRST SIGN-IN ON A SITE THE ACCOUNT WAS NOT CREATED ON. Recorded once per site in
// misc.touched (see misc.ts), which is how an account that uses both sites is told
// apart from one that uses either. On that first sign-in the account also gets this
// site's newsletter when it receives its home site's one: it said yes to mail over
// there, and now it uses this site too. Once only, so a later unsubscribe here holds.
// Never awaited by the caller and never throws: a failure here must not touch login.
async function noteFirstSignInHere(misc: Record<string, any>, userIncoming: any): Promise<void> {
	const home = homeSiteOf(misc);
	if (home === SITE) return;
	if (touchedSites(misc).some((t) => t.site === SITE)) return;
	let first = false;
	try {
		first = await touchSite(SITE);
	} catch (err) {
		console.error('[touched] could not record the sign-in:', err);
		return;
	}
	if (!first || !userIncoming?.email_verified) return;
	try {
		if (await getNewsletterSubscriptionStatus(SITE_NEWSLETTER_GROUP)) return;
		// "Subscribed at home": the home site's own group, or group 01 (Service Email),
		// the group every signup box joined before the sites had groups of their own.
		// The backfill moves those accounts onto their home group; this covers one it
		// has not reached yet.
		const homeGroup = home === 'bunnyquery' ? NEWSLETTER_GROUPS.bunnyquery.group : NEWSLETTER_GROUPS.skapi.group;
		const subscribedAtHome =
			(await getNewsletterSubscriptionStatus(homeGroup)) || (await getNewsletterSubscriptionStatus('authorized'));
		if (subscribedAtHome) {
			await setNewsletterSubscriptionStatus(true, SITE_NEWSLETTER_GROUP);
		}
	} catch (err) {
		console.error('[touched] could not carry the newsletter over:', err);
	}
}

export let userLoginCallback = async (userIncoming: any) => {
	// console.log('User login callback triggered with user:', userIncoming);
	if (userIncoming) {
		const incomingUserId = typeof userIncoming?.user_id === 'string' ? userIncoming.user_id : null;
		if (incomingUserId && lastLoggedInUserId && incomingUserId !== lastLoggedInUserId) {
			// User switched accounts within the same SPA lifecycle.
			// Clear project caches so route-resolved services cannot be reused
			// from a previous identity.
			while (serviceIdList.value.length > 0) {
				serviceIdList.value.pop();
			}
			for (let k in serviceList) {
				delete serviceList[k];
			}
			endOfList.value = false;
		}
		lastLoggedInUserId = incomingUserId;

		let userIncomingMisc = JSON.parse(userIncoming.misc || '{}');

		getStripeCustomer(userIncoming);

		// auto subscribe google users to newsletter
		if (
			userIncoming?.signup_ticket &&
			userIncoming?.signup_ticket === 'OIDPASS' &&
			!userIncomingMisc.googleUserSubscribed
		) {
			// This site's own newsletter, the group signup.vue signs a new account
			// up to; a Google account never passes through that form.
			await skapi.subscribeNewsletter({
				group: SITE_NEWSLETTER_GROUP,
			});
			userIncomingMisc.googleUserSubscribed = true;
			await miscUpdater({ googleUserSubscribed: true });
		}

		// After the Google block on purpose: it may have just stamped signup_origin.
		void noteFirstSignInHere(userIncomingMisc, userIncoming);
	} else {
		lastLoggedInUserId = null;
		while (serviceIdList.value.length > 0) {
			serviceIdList.value.pop();
		}
		for (let k in serviceList) {
			delete serviceList[k];
		}

		endOfList.value = false;
	}
};

let getStripeCustomer = async (user: any) => {
	let misc;

	if (user?.misc && typeof user.misc === 'string') {
		misc = JSON.parse(user.misc);
	} else {
		misc = {};
	}

	if (misc?.stripe_customer_id) {
		// stripe_customer_id exists

		let customer_id = misc.stripe_customer_id;

		// get customer info, and update customer.value
		customer = skapi.forwardRequest(null, {
			secretName: 'stripe_test',
			url: `https://api.stripe.com/v1/customers/${customer_id}`,
			method: 'GET',
			headers: {
				Authorization: 'Bearer $CLIENT_SECRET',
			},
		});
	} else {
		// stripe_customer_id does not exist

		// create customer, save customer id in user misc
		customer = skapi.forwardRequest(null, {
			secretName: 'stripe_test',
			url: 'https://api.stripe.com/v1/customers',
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				Authorization: 'Bearer $CLIENT_SECRET',
			},
			data: {
				name: user.name || user.email,
				email: user.email,
			},
		});

		// update customer id in user misc
		customer.then(async (c) => {
			if (c) {
				misc.stripe_customer_id = c.id;
				miscUpdater({
					stripe_customer_id: misc.stripe_customer_id,
				});
			}
		});
	}
};

/**
 * Opens the payment provider's customer portal for the signed-in account: invoices, payment
 * methods, the billing address and cancelling a plan. It is the Billing link www.skapi.com had.
 * The request goes through the host's stripe_test key, which the host's Stripe guard lets a
 * signed-in account use for its own customer only. The loader stays up while the browser leaves
 * the page; a failure takes it down and says so, instead of leaving the page covered for good.
 */
export async function openBillingPortal(returnPath: string = window.location.pathname) {
	fullScreenLoader();
	try {
		const resolved = await customer;
		if (!resolved?.id) {
			throw new Error('No billing account for this user yet.');
		}
		const session = await skapi.forwardRequest(null, {
			secretName: 'stripe_test',
			url: 'https://api.stripe.com/v1/billing_portal/sessions',
			method: 'POST',
			headers: {
				Authorization: 'Bearer $CLIENT_SECRET',
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			data: {
				customer: resolved.id,
				return_url: window.location.origin + returnPath,
			},
		});
		if (!session?.url) {
			throw new Error('The billing page did not open.');
		}
		// a Back from the billing page restores this page stale: reload it then
		markLeavingFor('billing');
		window.location.href = session.url;
	} catch (err) {
		console.error(err);
		removeFullScreenLoader();
		showToast('Could not open the billing page. Please try again.', 'error');
	}
}
